// Stores the app version hash
let currentVersion = null;
let notifiedVersion = null; // Track which version we've already notified about

/**
 * Generate a simple hash from the index.html content
 * We're looking at the script/link tags which will have build timestamps
 */
function getVersionHash(html) {
  // Extract the bundle.js and bundle.css references
  const jsMatch = html.match(/bundle\.js\?v=(\d+)/);
  const cssMatch = html.match(/bundle\.css\?v=(\d+)/);
  
  if (jsMatch && cssMatch) {
    return `${jsMatch[1]}-${cssMatch[1]}`;
  }
  return null;
}

/**
 * Check if a new version is available
 */
export async function checkForNewVersion() {
  try {
    // Fetch the current index.html from server (with cache-busting)
    const response = await fetch(`/?_=${Date.now()}`, {
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' }
    });
    
    if (!response.ok) return false;
    
    const html = await response.text();
    const serverVersion = getVersionHash(html);
    
    // Get current version from DOM if not set
    if (!currentVersion) {
      const scripts = document.querySelectorAll('script[src*="bundle.js"]');
      const links = document.querySelectorAll('link[href*="bundle.css"]');
      
      if (scripts.length > 0 && links.length > 0) {
        const jsMatch = scripts[0].src.match(/v=(\d+)/);
        const cssMatch = links[0].href.match(/v=(\d+)/);
        
        if (jsMatch && cssMatch) {
          currentVersion = `${jsMatch[1]}-${cssMatch[1]}`;
        }
      }
    }
    
    // Compare versions - ONLY alert if server version is NEWER than current
    if (serverVersion && currentVersion && serverVersion !== currentVersion) {
      // Extract timestamps (use first number from version string)
      const serverTimestamp = parseInt(serverVersion.split('-')[0]);
      const currentTimestamp = parseInt(currentVersion.split('-')[0]);
      
      // Only notify if server version is at least 15 minutes newer,
      // to tolerate rolling deployments across multiple instances
      const DEPLOY_TOLERANCE_MS = 15 * 60 * 1000;
      if ((serverTimestamp - currentTimestamp) > DEPLOY_TOLERANCE_MS && serverVersion !== notifiedVersion) {
        console.log('🎉 New version detected!', { 
          current: currentVersion, 
          currentTime: new Date(currentTimestamp).toISOString(),
          server: serverVersion,
          serverTime: new Date(serverTimestamp).toISOString()
        });
        return true;
      }
      
      // Log when current is newer (helpful for debugging)
      if (currentTimestamp > serverTimestamp) {
        console.log('ℹ️ Your local version is newer than server (dev build?)', {
          current: currentVersion,
          server: serverVersion
        });
      }
    }
    
    return false;
  } catch (error) {
    console.error('Error checking for new version:', error);
    return false;
  }
}

/**
 * Show a user-friendly prompt to reload
 */
export function promptForReload(serverVersion) {
  // Don't show multiple banners or if we've already notified about this version
  if (document.getElementById('version-update-banner') || serverVersion === notifiedVersion) {
    return;
  }

  // Mark this version as notified
  notifiedVersion = serverVersion;

  const banner = document.createElement('div');
  banner.id = 'version-update-banner';
  banner.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: #4CAF50;
    color: white;
    padding: 16px;
    text-align: center;
    font-family: system-ui, -apple-system, sans-serif;
    font-size: 14px;
    z-index: 999999;
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  `;
  
  banner.innerHTML = `
    <div style="display: flex; align-items: center; justify-content: center; gap: 16px; flex-wrap: wrap;">
      <span>🎉 A new version of BuildZilla is available!</span>
      <button id="reload-btn" style="
        background: white;
        color: #4CAF50;
        border: none;
        padding: 8px 20px;
        border-radius: 4px;
        font-weight: bold;
        cursor: pointer;
        font-size: 14px;
      ">Reload Now</button>
      <button id="dismiss-btn" style="
        background: transparent;
        color: white;
        border: 1px solid white;
        padding: 8px 20px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
      ">Later</button>
    </div>
  `;
  
  document.body.appendChild(banner);
  
  document.getElementById('reload-btn').addEventListener('click', () => {
    window.location.reload(true);
  });
  
  document.getElementById('dismiss-btn').addEventListener('click', () => {
    banner.remove();
    // Keep notifiedVersion set so we don't re-show for the same version
  });
}

/**
 * Start periodic version checking
 * @param {number} intervalMinutes - How often to check (default: 5 minutes)
 */
export function startVersionMonitoring(intervalMinutes = 5) {
  let isChecking = false; // Prevent concurrent checks
  
  const checkAndPrompt = async () => {
    // Prevent multiple simultaneous checks
    if (isChecking) return;
    
    isChecking = true;
    try {
      const newVersionAvailable = await checkForNewVersion();
      if (newVersionAvailable) {
        // Get the server version to pass to promptForReload
        const response = await fetch(`/?_=${Date.now()}`, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' }
        });
        
        if (response.ok) {
          const html = await response.text();
          const serverVersion = getVersionHash(html);
          if (serverVersion) {
            promptForReload(serverVersion);
          }
        }
      }
    } finally {
      isChecking = false;
    }
  };
  
  // Check every N minutes
  const interval = intervalMinutes * 60 * 1000;
  setInterval(checkAndPrompt, interval);
  
  // Also check when tab becomes visible (user comes back)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      checkAndPrompt();
    }
  });
  
  console.log(`✅ Version monitoring started (checking every ${intervalMinutes} minutes)`);
}

